import { useState } from 'react'
import { Button } from 'design-system/ui/atoms/button'
import {
  ArrowRight,
  Check,
  Copy,
  ExternalLink,
  Globe,
  Sparkles,
  Terminal,
  Zap,
} from 'lucide-react'

// Helper function to remove transport=sse from URL (same as URLPreview.tsx)
function removeTransportSSE(url: string): string {
  try {
    const urlObj = new URL(url)
    urlObj.searchParams.delete('transport')
    return urlObj.toString()
  } catch {
    // If URL parsing fails, try simple string replacement
    return url.replace(/[?&]transport=sse(&|$)/, (match, followingChar) => {
      return followingChar === '&' ? (match.startsWith('?') ? '?' : '&') : ''
    })
  }
}

interface ConnectInfoStepProps {
  selectedTab: string
  setSelectedTab: (tab: string) => void
  serverEndpoint?: string
  apiKey?: string
  connectedId?: string
  limitedActions: string[]
  generateCommand: () => void
}

const ConnectInfoStep = ({
  selectedTab,
  setSelectedTab,
  serverEndpoint,
  generateCommand,
}: ConnectInfoStepProps) => {
  const [copied, setCopied] = useState(false)

  const clients = [
    {
      id: 'cursor',
      name: 'Cursor',
      logo: 'https://static.cdnlogo.com/logos/c/23/cursor.svg',
      description: 'AI-first code editor',
      color: 'from-blue-500 to-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      textColor: 'text-blue-700',
    },
    {
      id: 'claude',
      name: 'Claude',
      logo: 'https://uxwing.com/wp-content/themes/uxwing/download/brands-and-social-media/claude-ai-icon.svg',
      description: "Anthropic's AI assistant",
      color: 'from-orange-500 to-orange-600',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200',
      textColor: 'text-orange-700',
    },
    {
      id: 'windsurf',
      name: 'Windsurf',
      logo: 'https://mcp.composio.dev/windsurf.svg',
      description: "Codeium's AI IDE",
      color: 'from-teal-500 to-teal-600',
      bgColor: 'bg-teal-50',
      borderColor: 'border-teal-200',
      textColor: 'text-teal-700',
    },
    {
      id: 'sse',
      name: 'SSE Stream',
      logo: 'lucide:zap',
      description: 'SSE Events streaming',
      color: 'from-indigo-500 to-indigo-600',
      bgColor: 'bg-indigo-50',
      borderColor: 'border-indigo-200',
      textColor: 'text-indigo-700',
    },
    {
      id: 'http',
      name: 'HTTP Stream',
      logo: 'lucide:globe',
      description: 'HTTP streaming protocol',
      color: 'from-red-500 to-red-600',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200',
      textColor: 'text-red-700',
    },
  ]

  const selectedClient = clients.find((c) => c.id === selectedTab)

  // Generate the appropriate URL/command based on selected client
  const getDisplayUrl = () => {
    if (!serverEndpoint) return ''

    // Remove existing query parameters and endpoints
    const urlWithoutQuery = serverEndpoint.split('?')[0] || ''
    const baseUrl = urlWithoutQuery.replace(/\/(sse|mcp)$/, '')

    switch (selectedTab) {
      case 'cursor': {
        const cleanUrl = removeTransportSSE(serverEndpoint)
        const urlWithMcp = cleanUrl.endsWith('/mcp')
          ? cleanUrl
          : cleanUrl + '/mcp'
        return `npx @composio/mcp@latest setup "${urlWithMcp}" --client cursor`
      }
      case 'claude': {
        const cleanUrl = removeTransportSSE(serverEndpoint)
        const urlWithMcp = cleanUrl.endsWith('/mcp')
          ? cleanUrl
          : cleanUrl + '/mcp'
        return `npx @composio/mcp@latest setup "${urlWithMcp}" --client claude`
      }
      case 'windsurf': {
        const cleanUrl = removeTransportSSE(serverEndpoint)
        const urlWithMcp = cleanUrl.endsWith('/mcp')
          ? cleanUrl
          : cleanUrl + '/mcp'
        return `npx @composio/mcp@latest setup "${urlWithMcp}" --client windsurf`
      }
      case 'sse':
        return `${baseUrl}/sse`
      case 'http':
        return `${baseUrl}/mcp`
      default:
        return serverEndpoint
    }
  }

  const displayUrl = getDisplayUrl()

  const handleCopy = async () => {
    if (displayUrl) {
      await navigator.clipboard.writeText(displayUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
      <div className="mx-auto max-w-5xl px-6 py-12">
        {/* Hero Section */}
        <div className="mb-16 text-center">
          <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-blue-100 to-purple-100 px-4 py-2 text-sm font-medium text-blue-800">
            <Sparkles className="h-4 w-4" />
            MCP Server Setup
          </div>
          <h1 className="mb-4 text-4xl font-bold text-gray-900">
            Connect Your Development Environment
          </h1>
          <p className="mx-auto max-w-2xl text-xl text-gray-600">
            Set up your MCP server in minutes with your preferred AI-powered
            development tool
          </p>
        </div>

        {/* Demo Video */}
        <div className="mb-16">
          <div className="group relative">
            <div className="absolute -inset-1 rounded-2xl bg-gradient-to-r from-blue-600 to-purple-600 opacity-25 blur transition duration-1000 group-hover:opacity-40"></div>
            <div className="relative overflow-hidden rounded-xl bg-white shadow-xl">
              <div className="bg-gradient-to-r from-gray-900 to-gray-800 px-6 py-4">
                <div className="flex items-center gap-2">
                  <div className="flex gap-2">
                    <div className="h-3 w-3 rounded-full bg-red-500"></div>
                    <div className="h-3 w-3 rounded-full bg-yellow-500"></div>
                    <div className="h-3 w-3 rounded-full bg-green-500"></div>
                  </div>
                  <span className="ml-4 text-sm text-gray-300">
                    MCP Setup Demo
                  </span>
                </div>
              </div>
              <video
                className="h-80 w-full object-cover"
                src="https://mcp-assets.composio.dev/mcp-cusor.mp4"
                autoPlay
                loop
                muted
                playsInline
              />
            </div>
          </div>
        </div>

        {/* Client Selection */}
        <div className="mb-12">
          <h2 className="mb-6 text-center text-2xl font-bold text-gray-900">
            Choose Your Client
          </h2>
          <div className="grid grid-cols-5 gap-3">
            {clients.map((client) => (
              <div
                key={client.id}
                onClick={() => setSelectedTab(client.id)}
                className="group relative w-full cursor-pointer transition-all duration-300"
              >
                <div
                  className={`absolute -inset-0.5 bg-gradient-to-r ${client.color} rounded-xl blur transition-all duration-300 ${
                    selectedTab === client.id
                      ? 'scale-102 opacity-40'
                      : 'opacity-0 group-hover:opacity-30'
                  }`}
                ></div>
                <div
                  className={`relative w-full rounded-xl border-2 bg-white shadow-lg transition-all duration-300 ${
                    selectedTab === client.id
                      ? `${client.borderColor} ${client.bgColor} border-opacity-80 shadow-lg`
                      : 'border-gray-200 hover:border-gray-300 hover:shadow-xl'
                  } p-4`}
                >
                  <div className="text-center">
                    <div
                      className={`mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl transition-all duration-300 ${
                        selectedTab === client.id
                          ? `${client.bgColor} border-2 ${client.borderColor} shadow-lg`
                          : 'border-2 border-gray-100 bg-gradient-to-br from-gray-50 to-white shadow-md hover:shadow-lg'
                      }`}
                    >
                      {client.logo.startsWith('lucide:') ? (
                        client.logo === 'lucide:zap' ? (
                          <Zap
                            className={`transition-all duration-300 ${
                              selectedTab === client.id
                                ? 'h-7 w-7 text-indigo-600'
                                : 'h-6 w-6 text-indigo-500'
                            }`}
                          />
                        ) : client.logo === 'lucide:globe' ? (
                          <Globe
                            className={`transition-all duration-300 ${
                              selectedTab === client.id
                                ? 'h-7 w-7 text-red-600'
                                : 'h-6 w-6 text-red-500'
                            }`}
                          />
                        ) : null
                      ) : (
                        <img
                          src={client.logo}
                          alt={client.name}
                          className={`object-contain transition-all duration-300 ${
                            selectedTab === client.id ? 'h-7 w-7' : 'h-6 w-6'
                          }`}
                        />
                      )}
                    </div>
                    <h3 className="mb-1 text-sm font-semibold text-gray-900 transition-all duration-300">
                      {client.name}
                    </h3>
                    <p className="text-xs leading-tight text-gray-600 transition-all duration-300">
                      {client.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Command Section */}
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl">
          <div className="p-8">
            {displayUrl ? (
              <div className="space-y-6">
                <div className="relative">
                  <div className="rounded-xl bg-gray-900 p-6 font-mono">
                    <div className="flex items-start justify-between gap-4">
                      <code className="flex-1 break-words text-sm leading-relaxed text-green-400">
                        {displayUrl}
                      </code>
                      <button
                        onClick={() => void handleCopy()}
                        className="flex flex-shrink-0 items-center gap-2 rounded-lg bg-gray-800 px-4 py-2 text-white transition-colors hover:bg-gray-700"
                      >
                        {copied ? (
                          <>
                            <Check className="h-4 w-4" />
                            Copied!
                          </>
                        ) : (
                          <>
                            <Copy className="h-4 w-4" />
                            Copy
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                <div
                  className={`p-6 ${selectedClient?.bgColor} border ${selectedClient?.borderColor} rounded-xl`}
                >
                  <div className="flex items-start gap-4">
                    <div
                      className={`h-8 w-8 bg-gradient-to-r ${selectedClient?.color} flex flex-shrink-0 items-center justify-center rounded-full`}
                    >
                      <ArrowRight className="h-4 w-4 text-white" />
                    </div>
                    <div>
                      <h4
                        className={`font-semibold ${selectedClient?.textColor} mb-2`}
                      >
                        Next Steps
                      </h4>
                      <p
                        className={`${selectedClient?.textColor} text-sm leading-relaxed`}
                      >
                        After running the command above, restart{' '}
                        {selectedClient?.name} to activate your MCP server
                        integration. You'll then have access to all the
                        configured actions and tools.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="py-12 text-center">
                <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-r from-blue-500 to-purple-600">
                  <Terminal className="h-10 w-10 text-white" />
                </div>
                <h4 className="mb-3 text-xl font-semibold text-gray-900">
                  Generate Your Setup Command
                </h4>
                <p className="mx-auto mb-8 max-w-md text-gray-600">
                  Click below to generate a secure, personalized command for
                  your MCP server setup
                </p>
                <Button
                  variant="primary"
                  size="40"
                  onClick={generateCommand}
                  className="px-8 py-4 text-lg font-medium"
                >
                  Generate Command
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="mt-12 text-center">
          <p className="text-sm text-gray-500">
            Need help? Check out our{' '}
            <a
              href="https://docs.composio.dev/mcp/introduction"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 font-medium text-blue-600 hover:text-blue-700"
            >
              documentation
              <ExternalLink className="h-3 w-3" />
            </a>
          </p>
        </div>
      </div>
    </div>
  )
}

export default ConnectInfoStep
